/**
*
* ¡¡¡¡¡¡¡IMPORTANT!!!!!!!  DO NOT USE THIS FILE DIRECTLY!!!
*
* Please use `utils/scroll.js` in this very same directory.
*
* This file was created to make unit testing easier by exposing methods previously internal to `utils/scroll.js`.
*
**/

// All events we want to listen to and call our `lazyLoadListener: Function`
const scrollishEvents = ['scroll', 'scrollend', 'touchcancel', 'touchend', 'touchmove', 'wheel'];
// All events we want to debounce.
// i.e. only allow the execution of `lazyLoadListener: Function` every Nms when any these events were fired
const debounceEvents = ['scroll', 'touchmove', 'wheel'].reduce((acc, evt) => ((acc[evt] = true) && acc), {});

// The number of milliseconds for which we'll allow `lazyLoadListener: Function` to call the `lazyLoadHandler: Function` in.
const debounceDelta = 50;
// Stores the last position of the scroll bars so we can tell if the Element being scrolled is still scrolling or not.
const lastScrollPosition = {element: null, x: undefined, y: undefined}; // eslint-disable-line no-undefined

// This is used to debounce the `lazyLoadListener: Function`.
// It's the last time the callback was fired by an event in `debounceEvents`
let lastFiredScroll = Date.now();
let lastFiredScrollEnd = Date.now();
let monitoringScroll = false;

export const SCROLLEND_EVENT = 'scrollend';
export const INTERNAL = {
    callbackFunction: () => {}
};

export function getLastScrollPosition() {
    return {...lastScrollPosition};
}

export function getScrollParent(el) {
    const clientHeight = el.clientHeight;
    const clientWidth = el.clientWidth;
    const scrollHeight = el.scrollHeight;
    const scrollWidth = el.scrollWidth;

    if (scrollHeight > clientHeight || scrollWidth > clientWidth) {
        return el;
    }

    const parentEl = el.parentElement;

    return (parentEl === document.documentElement || !parentEl)
        ? window
        : getScrollParent(parentEl);
}

export function debounceScrollHandler(evt) {
    if (monitoringScroll !== true) {
        return;
    }

    if (evt) {
        if (evt.type !== SCROLLEND_EVENT) {
            checkScrollEnd(evt.target); // eslint-disable-line no-use-before-define
        }

        // This Function may run multiple times from different events depending on device.
        // So we want to debounce this Function's execution on devices that will fire for more than one of the listed `debounceEvens`.
        if (debounceEvents[evt.type]) {
            const now = Date.now();

            if (now - lastFiredScroll < debounceDelta) {
                return;
            }

            lastFiredScroll = now;
        }
    }

    INTERNAL.callbackFunction(evt);
}

export function isScrollBeingMonitored() {
    return !!monitoringScroll;
}

export function monitorScroll(state = true) {
    monitoringScroll = !!state;
}

export function resetLastScrollPosition() {
    lastScrollPosition.element = null;
    lastScrollPosition.x = undefined; // eslint-disable-line no-undefined
    lastScrollPosition.y = undefined; // eslint-disable-line no-undefined
}

export function onScrollEnd() {
    if (typeof lastScrollPosition.x === 'undefined'
    || typeof lastScrollPosition.y === 'undefined'
    || lastScrollPosition.element === null) {
        return;
    }

    const now = Date.now();

    if (now - lastFiredScrollEnd < debounceDelta) {
        return;
    }

    lastFiredScrollEnd = now;

    const detail = {
        x: lastScrollPosition.x,
        y: lastScrollPosition.y
    };

    try {
        const evt = new window.CustomEvent(SCROLLEND_EVENT, {
            bubbles: true,
            cancelable: true,
            detail,
            type: SCROLLEND_EVENT
        });

        lastScrollPosition.element.dispatchEvent(evt);
    } catch (e) {
        // MSIE 11 doesn't support `new CustomEvent()` so just run the handler if it errors out...
        const evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(SCROLLEND_EVENT, true, true, detail);

        lastScrollPosition.element.dispatchEvent(evt);
    }

    resetLastScrollPosition();
}

export function onScrollChange(el, x, y) {
    if (typeof x === 'undefined' || typeof y === 'undefined') {
        return;
    }

    if (lastScrollPosition.element !== el || (lastScrollPosition.x === x && lastScrollPosition.y === y)) {
        onScrollEnd();
    }

    if (lastScrollPosition.element !== null) {
        setImmediate(() => checkScrollEnd(el)); // eslint-disable-line no-use-before-define
    }

    if (el !== null) {
        lastScrollPosition.element = el;
        lastScrollPosition.x = x;
        lastScrollPosition.y = y;
    }
}

export function checkScrollEnd(el) {
    if (!el) {
        return;
    }

    const scrollParent = getScrollParent(el);

    if (scrollParent) {
        const x = scrollParent.scrollLeft;
        const y = scrollParent.scrollTop;
        if (lastScrollPosition.element === scrollParent) {
            if (lastScrollPosition.x === x && lastScrollPosition.y === y) {
                onScrollEnd();
            } else {
                onScrollChange(scrollParent, x, y);
            }
        } else {
            onScrollChange(scrollParent, x, y);
        }
    } else if (lastScrollPosition.element !== null) {
        onScrollEnd();
    }
}

if (typeof navigator !== 'undefined') {
    scrollishEvents.forEach((event) => {
        addEventListener(event, debounceScrollHandler, true);
    });

    addEventListener('beforeunload', () => {
        scrollishEvents.forEach((event) =>
            removeEventListener(event, debounceScrollHandler, true));
    }, true);
}
